import { Injectable, inject, DOCUMENT } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, tap } from 'rxjs';
import { Router } from '@angular/router';
import {
  PractiseBookPageActions,
  PractiseHomePageActions,
  PractiseOverviewPageActions,
} from './practise.actions';

import { isDefined } from '@lyceo/shared/util';
import { BookFacade } from '../book/book.facade';

@Injectable()
export class PractiseEffects {
  private readonly actions$ = inject(Actions);
  private readonly router: Router = inject(Router);
  private readonly document: Document = inject<Document>(DOCUMENT);
  private readonly bookFacade = inject(BookFacade);

  public emptyCardClicked$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(PractiseHomePageActions.emptyCardClicked),
        map(() => {
          this.router.navigate(['/practise']);
        }),
      );
    },
    { dispatch: false },
  );

  public cardClickedNavigate$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          PractiseOverviewPageActions.cardClicked,
          PractiseHomePageActions.cardClicked,
        ),
        map((action) => {
          if (action.card.personUsesBook) {
            this.router.navigate([
              '/practise',
              'book',
              action.card.personUsesBook.bookId,
              'paragraph',
              action.card.personUsesBook.lastUsedParagraphId,
            ]);
          }
        }),
      );
    },
    { dispatch: false },
  );

  public paragraphClickedNavigate$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          PractiseBookPageActions.paragraphClicked,
          PractiseBookPageActions.paginationParagraphClicked,
          PractiseBookPageActions.paginationLastParagraphClicked,
        ),
        map((action) => {
          this.router.navigate([
            '/practise',
            'book',
            action.paragraph.bookId,
            'paragraph',
            action.paragraph.id,
          ]);
        }),
      );
    },
    { dispatch: false },
  );

  public paragraphSearchClickedNavigate$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(PractiseBookPageActions.searchParagraphClicked),
        map(({ item }) => ({ item, bookId: this.bookFacade.paramBookId() })),
        filter((data) => isDefined(data.item) && isDefined(data.bookId)),
        tap(({ item, bookId }) => {
          this.router.navigate([
            '/practise',
            'book',
            bookId,
            'paragraph',
            item?.value,
          ]);
        }),
      );
    },
    { dispatch: false },
  );

  public scrollIntoView$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          PractiseBookPageActions.paragraphClicked,
          PractiseBookPageActions.paginationParagraphClicked,
          PractiseBookPageActions.paginationLastParagraphClicked,
          PractiseBookPageActions.searchParagraphClicked,
        ),
        tap(() => {
          setTimeout(() => {
            this.document.querySelector('.content-container')?.scrollTo({
              behavior: 'smooth',
              top: 0,
              left: 0,
            });
          });
        }),
      );
    },
    { dispatch: false },
  );

  public finalExamNavigate$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          PractiseBookPageActions.finalExamClicked,
          PractiseBookPageActions.paginationFinalExamClicked,
        ),
        map((action) => {
          this.router.navigate([
            '/practise',
            'book',
            action.book.id,
            'final-exam',
          ]);
        }),
      );
    },
    { dispatch: false },
  );
}
